import { useEffect, useId, useRef, useState } from "react";
import { limpiarEstadoDeSesion } from "../lib/estadoDeSesion";

/**
 * Devuelve el kiosco a su portada cuando nadie lo toca.
 *
 * NO CIERRA SESIÓN, y es la decisión de fondo de este modo. La cuenta la abre la
 * bibliotecaria al empezar la jornada; un cierre por inactividad la obligaría a
 * re-entrar todo el día y acabaría con la contraseña en un papel pegado al
 * monitor — que es peor que no tener temporizador.
 *
 * RECARGA LA PÁGINA, y no es pereza. `navigate("/servicios")` no desmonta nada si
 * el visitante YA estaba en Servicios, y `limpiarEstadoDeSesion()` solo alcanza a
 * los módulos registrados — no al estado de un componente cualquiera: un filtro
 * puesto, un formulario a medias, un modal abierto. El siguiente encontraría la
 * pantalla donde la dejó el anterior.
 *
 * La recarga es lo único que garantiza pantalla limpia, y aquí es gratis:
 * `ikids.session` y el token viven en `sessionStorage`, que SOBREVIVE a una
 * recarga en la misma pestaña. La sesión de Cognito sigue abierta y la
 * bibliotecaria no tiene que hacer nada.
 *
 * AVISA ANTES DE RECARGAR (WCAG 2.2.1, "Tiempo ajustable"). Borrar la pantalla
 * sin aviso castigaba justo a quien más tarda: el niño que lee despacio, el
 * adulto que rellena una solicitud con un lector de pantalla. Treinta segundos
 * antes aparece "¿Sigues ahí?" con una cuenta atrás y un botón grande; tocarlo
 * —o tocar cualquier cosa— vuelve a empezar los cinco minutos. Si nadie
 * responde, se recarga igual que antes: el aviso no cambia el destino, solo da
 * la oportunidad de evitarlo.
 */
/** Segundos de aviso antes de recargar. */
const AVISO_SEGUNDOS = 30;

export function KioscoIdle({
  minutos = 5, destino,
}: { minutos?: number; destino: string }) {
  const timerAviso = useRef<number | undefined>(undefined);
  const timerReset = useRef<number | undefined>(undefined);
  const intervalo = useRef<number | undefined>(undefined);
  // `null` = sin aviso. Si no, los segundos que faltan para recargar.
  const [restantes, setRestantes] = useState<number | null>(null);
  const boton = useRef<HTMLButtonElement>(null);
  // Dónde estaba el foco antes del aviso, para devolverlo al cerrarlo: quien
  // navega con teclado no debería reaparecer al principio de la página.
  const focoPrevio = useRef<HTMLElement | null>(null);
  const idTitulo = useId();
  const idTexto = useId();
  // El botón necesita llamar al mismo `reiniciar` que los eventos globales.
  const reiniciarRef = useRef<() => void>(() => {});

  useEffect(() => {
    const total = minutos * 60 * 1000;
    // Con un `minutos` menor que el aviso (solo en pruebas), el aviso sale ya.
    const aviso = Math.max(0, total - AVISO_SEGUNDOS * 1000);

    const limpiar = () => {
      window.clearTimeout(timerAviso.current);
      window.clearTimeout(timerReset.current);
      window.clearInterval(intervalo.current);
    };

    const reiniciar = () => {
      limpiar();
      setRestantes(null);
      timerAviso.current = window.setTimeout(() => {
        // La cuenta se calcula contra un instante fijo y no restando 1 en cada
        // tic: un `setInterval` en una pestaña cargada se atrasa, y el número
        // en pantalla acabaría diciendo "5 segundos" cuando ya no queda ninguno.
        const fin = Date.now() + (total - aviso);
        const tic = () => setRestantes(Math.max(0, Math.ceil((fin - Date.now()) / 1000)));
        focoPrevio.current = document.activeElement as HTMLElement | null;
        tic();
        intervalo.current = window.setInterval(tic, 1000);
      }, aviso);
      timerReset.current = window.setTimeout(() => {
        limpiar();
        limpiarEstadoDeSesion();
        // El destino ANTES de recargar: al volver, arranca en la portada.
        //
        // RECIBIDO, no escrito aquí a mano. "/servicios" estaba repetido en
        // dos sitios sin relación —aquí y en `home()` de App.tsx— y nada
        // avisaba si uno cambiaba sin el otro: el día que la portada del
        // kiosco deje de ser Servicios, el reseteo mandaría al visitante a una
        // sala que ya no es la suya. `home(session)` sigue siendo la única
        // fuente de verdad de a dónde va cada rol.
        window.location.hash = `#${destino}`;
        window.location.reload();
      }, total);
    };
    // Cualquier interacción reinicia, también DENTRO del aviso: un toque en
    // el fondo oscuro vale igual que el botón. El botón existe para que haya un
    // objetivo claro y alcanzable con teclado, no porque sea el único camino.
    reiniciarRef.current = reiniciar;
    const eventos = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    for (const e of eventos) window.addEventListener(e, reiniciar, { passive: true });
    reiniciar();
    return () => {
      limpiar();
      for (const e of eventos) window.removeEventListener(e, reiniciar);
    };
  }, [minutos, destino]);

  const visible = restantes !== null;
  useEffect(() => {
    if (visible) {
      // El foco al botón: un `alertdialog` sin foco dentro no lo anuncian todos
      // los lectores, y con teclado bastan Enter o Espacio para seguir.
      boton.current?.focus();
    } else if (focoPrevio.current) {
      const previo = focoPrevio.current;
      focoPrevio.current = null;
      if (previo.isConnected) previo.focus();
    }
  }, [visible]);

  if (!visible) return null;

  // Reutiliza `.kiosco-dialog-overlay`/`.kiosco-dialog` (sedes.css) en vez de
  // depender de un componente de diálogo compartido: este aviso tiene que
  // funcionar aunque el resto de la interfaz cambie. Sin `onClick` en el
  // fondo: el `pointerdown` global ya reinicia, y un clic no debe hacer más.
  return (
    <div className="kiosco-dialog-overlay">
      <div
        className="kiosco-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={idTexto}
        style={{ textAlign: "center", maxWidth: 440 }}
      >
        <h2 id={idTitulo} style={{ margin: "0 0 10px", fontSize: "var(--t-lg)" }}>
          ¿Sigues ahí?
        </h2>
        <p id={idTexto} style={{ margin: "0 0 20px", fontSize: "var(--t-md)" }}>
          La pantalla se reiniciará en{" "}
          {/* `role="timer"` no se anuncia en cada tic (aria-live off por
              defecto): leer "29, 28, 27…" en voz alta taparía todo lo demás. */}
          <strong role="timer">{restantes}</strong> segundos
        </p>
        <button
          ref={boton}
          type="button"
          className="btn dark"
          // Grande a propósito: es un kiosco táctil y lo va a tocar un niño.
          style={{ minHeight: 56, minWidth: 200, fontSize: "var(--t-md)" }}
          // Además de los eventos globales: la "activación" de un lector de
          // pantalla (VoiceOver, TalkBack) dispara `click` sin `pointerdown` ni
          // `keydown`, y sin esto el botón no haría nada para quien más lo usa.
          onClick={() => reiniciarRef.current()}
        >
          Seguir aquí
        </button>
      </div>
    </div>
  );
}
